/**
 * @matchday/client — auth.ts
 *
 * Client auth: OTP request/verify (own MessagingService, Supabase-auth-adjacent,
 * KICKOFF §2.3), magic-link silent token exchange (/m/{id}?t=…), and the 90-day
 * sliding-session helper.
 *
 * All request/response shapes are zod-validated at the boundary and come from
 * @matchday/contracts (OtpRequestAck/ApiError ratified as contracts v0.2 —
 * the earlier local mirrors are re-exported for compatibility).
 */
import {
  ApiError,
  AuthSession,
  OtpRequest,
  OtpRequestAck,
  OtpVerify,
  type IsoTimestamp,
} from "@matchday/contracts";
import { z } from "zod";

export { OtpRequestAck };
export const ApiErrorBody = ApiError;
export type ApiErrorBody = ApiError;

export class AuthApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail?: unknown,
  ) {
    super(`auth api error ${status}: ${code}`);
    this.name = "AuthApiError";
  }
}

// ── Session storage (injectable — localStorage/IndexedDB adapter later) ──────

export interface SessionStore {
  load(): Promise<AuthSession | null>;
  save(session: AuthSession): Promise<void>;
  clear(): Promise<void>;
}

export class InMemorySessionStore implements SessionStore {
  private session: AuthSession | null = null;

  async load(): Promise<AuthSession | null> {
    return this.session;
  }

  async save(session: AuthSession): Promise<void> {
    this.session = session;
  }

  async clear(): Promise<void> {
    this.session = null;
  }
}

// ── Client ───────────────────────────────────────────────────────────────────

export const SESSION_SLIDING_WINDOW_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

export interface AuthClientOptions {
  /** Base URL of the messaging service, e.g. "http://localhost:8788". */
  baseUrl: string;
  fetchImpl?: typeof fetch;
  storage?: SessionStore;
  now?: () => Date;
  /** Idempotency-key source; injectable for deterministic harness runs. */
  uuid?: () => string;
  /** Sent as x-device-hash so the issued session binds to this device. */
  deviceHash?: string;
}

export interface AuthClient {
  /** POST /auth/otp/request with a fresh idempotency key. */
  requestOtp(msisdn: string): Promise<{ ack: OtpRequestAck; idempotencyKey: string }>;
  /** POST /auth/otp/verify — on success stores and returns the AuthSession. */
  verifyOtp(msisdn: string, code: string): Promise<AuthSession>;
  /**
   * Silent token exchange for signed single-use deep links `/m/{id}?t=…`:
   * the app shell pulls `t` off the URL and exchanges it without any visible
   * auth step. Stores and returns the AuthSession.
   */
  exchangeMagicLink(token: string): Promise<AuthSession>;
  /**
   * 90-day sliding refresh: re-anchors last_seen_at to now and slides
   * expires_at to now + 90d, persisting the result. Call on app foreground.
   * Returns null (and clears storage) if the session has already expired.
   * NOTE: server-side slide piggybacks on authenticated requests; a dedicated
   * /auth/session/refresh contract is a filed contract-change request.
   */
  touchSession(): Promise<AuthSession | null>;
  /** Load the stored session; null if absent or expired (expired ⇒ cleared). */
  getSession(): Promise<AuthSession | null>;
  signOut(): Promise<void>;
}

export function createAuthClient(options: AuthClientOptions): AuthClient {
  const fetchImpl = options.fetchImpl ?? fetch;
  const storage = options.storage ?? new InMemorySessionStore();
  const now = options.now ?? (() => new Date());
  const uuid = options.uuid ?? (() => crypto.randomUUID());
  const base = options.baseUrl.replace(/\/$/, "");

  async function post(path: string, body: unknown): Promise<Response> {
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (options.deviceHash !== undefined) headers["x-device-hash"] = options.deviceHash;
    return fetchImpl(`${base}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
  }

  async function throwApiError(res: Response): Promise<never> {
    let code = "unknown_error";
    let detail: unknown;
    try {
      const parsed = ApiErrorBody.safeParse(await res.json());
      if (parsed.success) {
        code = parsed.data.error;
        detail = parsed.data.detail;
      }
    } catch {
      // non-JSON error body — keep the fallback code
    }
    throw new AuthApiError(res.status, code, detail);
  }

  async function parseAndStoreSession(res: Response): Promise<AuthSession> {
    const session = AuthSession.parse(await res.json());
    await storage.save(session);
    return session;
  }

  function iso(date: Date): IsoTimestamp {
    return date.toISOString();
  }

  return {
    async requestOtp(msisdn) {
      const idempotencyKey = uuid();
      const body = OtpRequest.parse({ msisdn, idempotency_key: idempotencyKey });
      const res = await post("/auth/otp/request", body);
      if (!res.ok) await throwApiError(res);
      const ack = OtpRequestAck.parse(await res.json());
      return { ack, idempotencyKey };
    },

    async verifyOtp(msisdn, code) {
      const body = OtpVerify.parse({ msisdn, code, idempotency_key: uuid() });
      const res = await post("/auth/otp/verify", body);
      if (!res.ok) await throwApiError(res);
      return parseAndStoreSession(res);
    },

    async exchangeMagicLink(token) {
      const res = await post("/auth/magiclink/redeem", { token });
      if (!res.ok) await throwApiError(res);
      return parseAndStoreSession(res);
    },

    async touchSession() {
      const session = await storage.load();
      if (session === null) return null;
      const at = now();
      if (new Date(session.expires_at).getTime() <= at.getTime()) {
        await storage.clear();
        return null;
      }
      const slid = AuthSession.parse({
        ...session,
        last_seen_at: iso(at),
        expires_at: iso(new Date(at.getTime() + SESSION_SLIDING_WINDOW_MS)),
      });
      await storage.save(slid);
      return slid;
    },

    async getSession() {
      const session = await storage.load();
      if (session === null) return null;
      if (new Date(session.expires_at).getTime() <= now().getTime()) {
        await storage.clear();
        return null;
      }
      return session;
    },

    async signOut() {
      await storage.clear();
    },
  };
}
